import React, { useEffect, useRef, useState } from 'react'
import { Canvas, PencilBrush, Rect, Circle, Triangle, Line, FabricImage } from 'fabric'
import { HexColorPicker } from 'react-colorful'

const DrawingTools = ({ image, onDrawingComplete }) => {
  const canvasRef = useRef(null)
  const fabricCanvasRef = useRef(null)
  const [canvas, setCanvas] = useState(null)
  const [drawingMode, setDrawingMode] = useState('pencil')
  const [brushColor, setBrushColor] = useState('#000000')
  const [brushSize, setBrushSize] = useState(5)
  const [showColorPicker, setShowColorPicker] = useState(false)

  useEffect(() => {
    if (!canvasRef.current) return

    const fabricCanvas = new Canvas(canvasRef.current, {
      isDrawingMode: true,
      backgroundColor: 'white'
    })

    fabricCanvasRef.current = fabricCanvas
    setCanvas(fabricCanvas)

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const maxWidth = 1400
      const maxHeight = 1000
      let width = img.width
      let height = img.height

      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }
      if (height > maxHeight) {
        width = (width * maxHeight) / height
        height = maxHeight
      }

      fabricCanvas.setWidth(width)
      fabricCanvas.setHeight(height)

      FabricImage.fromURL(image, { crossOrigin: 'anonymous' }).then((fabricImg) => {
        fabricImg.scaleToWidth(width)
        fabricImg.scaleToHeight(height)
        fabricImg.selectable = false
        fabricCanvas.backgroundImage = fabricImg
        fabricCanvas.renderAll()
      })
    }
    img.src = image

    return () => {
      fabricCanvas.dispose()
    }
  }, [image])

  useEffect(() => {
    if (!canvas) return

    if (canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.color = brushColor
      canvas.freeDrawingBrush.width = brushSize
    }

    if (drawingMode === 'pencil') {
      canvas.isDrawingMode = true
      canvas.freeDrawingBrush = new PencilBrush(canvas)
      canvas.freeDrawingBrush.color = brushColor
      canvas.freeDrawingBrush.width = brushSize
    } else if (drawingMode === 'eraser') {
      canvas.isDrawingMode = true
      canvas.freeDrawingBrush = new PencilBrush(canvas)
      canvas.freeDrawingBrush.color = 'white'
      canvas.freeDrawingBrush.width = brushSize * 2
    } else {
      canvas.isDrawingMode = false
    }
  }, [canvas, drawingMode, brushColor, brushSize])

  const addShape = (shapeType) => {
    if (!canvas) return

    let shape
    const centerX = canvas.width / 2
    const centerY = canvas.height / 2

    switch (shapeType) {
      case 'rectangle':
        shape = new Rect({
          left: centerX - 50,
          top: centerY - 50,
          width: 100,
          height: 100,
          fill: 'transparent',
          stroke: brushColor,
          strokeWidth: 3
        })
        break
      case 'circle':
        shape = new Circle({
          left: centerX - 50,
          top: centerY - 50,
          radius: 50,
          fill: 'transparent',
          stroke: brushColor,
          strokeWidth: 3
        })
        break
      case 'triangle':
        shape = new Triangle({
          left: centerX - 50,
          top: centerY - 50,
          width: 100,
          height: 100,
          fill: 'transparent',
          stroke: brushColor,
          strokeWidth: 3
        })
        break
      case 'line':
        shape = new Line([centerX - 50, centerY, centerX + 50, centerY], {
          stroke: brushColor,
          strokeWidth: 3
        })
        break
      default:
        break
    }

    if (shape) {
      canvas.add(shape)
      canvas.renderAll()
    }
  }

  const handleClear = () => {
    if (!canvas) return
    canvas.getObjects().forEach((obj) => {
      if (obj !== canvas.backgroundImage) {
        canvas.remove(obj)
      }
    })
    canvas.renderAll()
  }

  const handleUndo = () => {
    if (!canvas) return
    const objects = canvas.getObjects()
    if (objects.length > 0) {
      canvas.remove(objects[objects.length - 1])
      canvas.renderAll()
    }
  }

  const handleApply = () => {
    if (!canvas) return
    const dataURL = canvas.toDataURL({
      format: 'png',
      quality: 1
    })
    onDrawingComplete(dataURL)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setDrawingMode('pencil')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            drawingMode === 'pencil'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Pencil
        </button>
        <button
          onClick={() => setDrawingMode('eraser')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            drawingMode === 'eraser'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Eraser
        </button>
        <button
          onClick={() => addShape('rectangle')}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          Rectangle
        </button>
        <button
          onClick={() => addShape('circle')}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          Circle
        </button>
        <button
          onClick={() => addShape('triangle')}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          Triangle
        </button>
        <button
          onClick={() => addShape('line')}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          Line
        </button>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <div
              className="w-6 h-6 rounded border-2 border-gray-400"
              style={{ backgroundColor: brushColor }}
            />
            <span className="font-medium text-gray-700">Color</span>
          </button>
          {showColorPicker && (
            <div className="absolute z-10 mt-2">
              <div
                className="fixed inset-0"
                onClick={() => setShowColorPicker(false)}
              />
              <div className="relative bg-white p-3 rounded-lg shadow-lg">
                <HexColorPicker color={brushColor} onChange={setBrushColor} />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Size:</label>
          <input
            type="range"
            min="1"
            max="50"
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-sm font-medium text-gray-700 w-8">{brushSize}</span>
        </div>
      </div>

      <div className="flex justify-center bg-gray-100 rounded-lg p-4">
        <canvas ref={canvasRef} className="border border-gray-300 rounded" />
      </div>

      <div className="flex gap-2 justify-center">
        <button
          onClick={handleUndo}
          className="px-4 py-2 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-colors"
        >
          Undo
        </button>
        <button
          onClick={handleClear}
          className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
        >
          Clear All
        </button>
        <button
          onClick={handleApply}
          className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
        >
          Apply Drawing
        </button>
      </div>
    </div>
  )
}

export default DrawingTools
